import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Wallet, Download, RefreshCw, TrendingUp, Receipt, CalendarDays, FileText } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../lib/api";

const fmt = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

const COMPANY = {
  name: "PT. RUANG KARYA TEKNOLOGI",
  npwp: "10.000.000.1-091.210.3",
  addr1: "Dsn. Sugihan, Ds./Kel. Cerme, Kec. Grogol,",
  addr2: "Kab. Kediri, Provinsi Jawa Timur",
  email: "ruangkaryateknologi@skorup.io",
  phone: "0851-2106-8525",
};

const loadLogo = () => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/jpeg", 0.9));
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = "/brand/rkt_logo.png";
});

const PERIODS = [
  { id: "day", label: "Harian" },
  { id: "week", label: "Mingguan" },
  { id: "month", label: "Bulanan" },
  { id: "annual", label: "Tahunan (SPT)" },
];

const yearNow = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => yearNow - 4 + i);

export default function AdminRevenue() {
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [monthSel, setMonthSel] = useState("");
  const [data, setData] = useState(null);
  const [year, setYear] = useState(yearNow);
  const [annual, setAnnual] = useState(null);

  const load = (p = period, df = from, dt = to) => {
    setData(null);
    const params = new URLSearchParams({ period: p });
    if (df) params.set("date_from", df);
    if (dt) params.set("date_to", dt);
    api.get(`/admin/revenue-report?${params.toString()}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  };
  const loadAnnual = (y = year) => {
    setAnnual(null);
    api.get(`/admin/annual-report?year=${y}`).then(({ data }) => setAnnual(data)).catch(() => setAnnual({ error: true }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const preset = (kind) => {
    const now = new Date();
    let df = "", dt = now.toISOString().slice(0, 10);
    if (kind === "thisMonth") df = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    else if (kind === "thisYear") df = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
    else if (kind === "all") { df = ""; dt = ""; }
    setMonthSel(""); setFrom(df); setTo(dt); load(period, df, dt);
  };

  const pickMonth = (val) => {
    if (!val) { setMonthSel(""); return; }
    const [y, m] = val.split("-").map(Number);
    const df = `${val}-01`;
    const last = new Date(y, m, 0).getDate();
    const dt = `${val}-${String(last).padStart(2, "0")}`;
    setMonthSel(val); setFrom(df); setTo(dt); setPeriod("month");
    load("month", df, dt);
  };

  const exportCSV = () => {
    if (!data || !data.series) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(["Laporan Pendapatan SkorUp.io"].map(esc).join(","));
    lines.push([`Periode: ${PERIODS.find((p) => p.id === period)?.label}`].map(esc).join(","));
    lines.push([`Rentang: ${from || "Awal"} s/d ${to || "Sekarang"}`].map(esc).join(","));
    lines.push([]);
    lines.push(["Periode", "Jumlah Transaksi", "Pendapatan (Rp)"].map(esc).join(","));
    data.series.forEach((s) => lines.push([s.label, s.count, Math.round(s.revenue)].map(esc).join(",")));
    lines.push([]);
    lines.push(["TOTAL", data.summary.total_transactions, Math.round(data.summary.total_revenue)].map(esc).join(","));
    lines.push([]);
    lines.push(["Ringkasan Pajak - PPh Final UMKM"].map(esc).join(","));
    lines.push(["Total Omzet (Peredaran Bruto)", "", Math.round(data.summary.total_revenue)].map(esc).join(","));
    lines.push(["PPh Final UMKM (0,5%)", "", -Math.round(data.summary.total_revenue * 0.005)].map(esc).join(","));
    lines.push(["Estimasi Pendapatan Bersih", "", Math.round(data.summary.total_revenue * 0.995)].map(esc).join(","));
    lines.push(["*PPh Final UMKM 0,5% sesuai PP No. 55 Tahun 2022 atas peredaran bruto"].map(esc).join(","));
    lines.push([]);
    lines.push(["Rincian per Metode Pembayaran"].map(esc).join(","));
    lines.push(["Metode", "Transaksi", "Pendapatan (Rp)"].map(esc).join(","));
    (data.by_gateway || []).forEach((g) => lines.push([g.gateway, g.count, Math.round(g.revenue)].map(esc).join(",")));
    lines.push([]);
    lines.push(["Rincian per Produk"].map(esc).join(","));
    lines.push(["Produk", "Transaksi", "Pendapatan (Rp)"].map(esc).join(","));
    (data.by_product || []).forEach((p) => lines.push([p.product, p.count, Math.round(p.revenue)].map(esc).join(",")));
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `laporan-pendapatan-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!data || !data.series) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const NAVY = [16, 42, 82], CYAN = [0, 150, 180], GRAY = [110, 120, 135];
    const logo = await loadLogo();
    if (logo) { try { doc.addImage(logo, "JPEG", 15, 12, 24, 24); } catch { /* ignore */ } }
    doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...NAVY);
    doc.text(COMPANY.name, 43, 18);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(COMPANY.addr1, 43, 24);
    doc.text(COMPANY.addr2, 43, 28);
    doc.text(`NPWP: ${COMPANY.npwp}`, 43, 32);
    doc.text(`${COMPANY.email}  |  ${COMPANY.phone}`, 43, 36);
    doc.setDrawColor(...CYAN); doc.setLineWidth(0.8); doc.line(15, 40, 195, 40);

    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(...NAVY);
    doc.text("LAPORAN PENDAPATAN", 15, 50);
    const periodLabel = PERIODS.find((p) => p.id === period)?.label;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(`Periode  : ${periodLabel}`, 15, 57);
    doc.text(`Rentang  : ${from || "Awal"}  s/d  ${to || "Sekarang"}`, 15, 61.5);
    doc.text(`Dicetak  : ${new Date().toLocaleString("id-ID")}`, 15, 66);

    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...NAVY);
    doc.text(`Total Pendapatan: ${fmt(data.summary.total_revenue)}`, 120, 57);
    doc.text(`Transaksi Berhasil: ${data.summary.total_transactions}`, 120, 61.5);
    doc.text(`Rata-rata/Transaksi: ${fmt(data.summary.avg_transaction)}`, 120, 66);

    autoTable(doc, {
      startY: 72,
      head: [["Periode", "Jumlah Transaksi", "Pendapatan"]],
      body: data.series.map((s) => [s.label, String(s.count), fmt(s.revenue)]),
      foot: [["TOTAL", String(data.summary.total_transactions), fmt(data.summary.total_revenue)]],
      theme: "grid",
      headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: CYAN, textColor: 255, fontStyle: "bold" },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
      styles: { fontSize: 9, cellPadding: 2.5 },
      margin: { left: 15, right: 15 },
    });

    let y = doc.lastAutoTable.finalY + 8;
    if (data.by_gateway?.length) {
      autoTable(doc, {
        startY: y,
        head: [["Metode Pembayaran", "Transaksi", "Pendapatan"]],
        body: data.by_gateway.map((g) => [g.gateway, String(g.count), fmt(g.revenue)]),
        theme: "striped",
        headStyles: { fillColor: [55, 65, 81], textColor: 255 },
        columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
        styles: { fontSize: 8.5, cellPadding: 2 },
        margin: { left: 15, right: 110 },
        tableWidth: 90,
      });
    }
    if (data.by_product?.length) {
      autoTable(doc, {
        startY: y,
        head: [["Produk", "Transaksi", "Pendapatan"]],
        body: data.by_product.map((p) => [p.product, String(p.count), fmt(p.revenue)]),
        theme: "striped",
        headStyles: { fillColor: [55, 65, 81], textColor: 255 },
        columnStyles: { 1: { halign: "center" }, 2: { halign: "right" } },
        styles: { fontSize: 8.5, cellPadding: 2 },
        margin: { left: 110, right: 15 },
        tableWidth: 85,
      });
    }

    y = doc.lastAutoTable.finalY + 8;
    const gross = data.summary.total_revenue;
    const pph = Math.round(gross * 0.005);
    const net = gross - pph;
    autoTable(doc, {
      startY: y,
      head: [["Ringkasan Pajak - PPh Final UMKM", "Jumlah"]],
      body: [
        ["Total Omzet (Peredaran Bruto)", fmt(gross)],
        ["PPh Final UMKM (0,5% x omzet bruto)", "- " + fmt(pph)],
      ],
      foot: [["Estimasi Pendapatan Bersih (setelah PPh)", fmt(net)]],
      theme: "grid",
      headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: [34, 160, 90], textColor: 255, fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" } },
      styles: { fontSize: 9.5, cellPadding: 2.8 },
      margin: { left: 15, right: 15 },
    });
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(...GRAY);
    doc.text("*PPh Final UMKM 0,5% sesuai PP No. 55 Tahun 2022 atas peredaran bruto (omzet). Nilai bersifat estimasi.",
      15, doc.lastAutoTable.finalY + 4);

    y = doc.lastAutoTable.finalY + 16;
    const ph = doc.internal.pageSize.getHeight();
    if (y > ph - 30) { doc.addPage(); y = 30; }
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text("Hormat kami,", 150, y, { align: "center" });
    doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY);
    doc.text(COMPANY.name, 150, y + 18, { align: "center" });
    doc.setFontSize(7.5); doc.setTextColor(...GRAY); doc.setFont("helvetica", "normal");
    doc.text("Dokumen laporan pendapatan ini dihasilkan otomatis oleh sistem untuk keperluan pembukuan dan pelaporan pajak.", 15, ph - 12);
    doc.text(`${COMPANY.name}  |  ${COMPANY.email}  |  ${COMPANY.phone}`, 15, ph - 8);

    doc.save(`laporan-pendapatan-${period}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportAnnualPDF = async () => {
    if (!annual || !annual.months) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const NAVY = [16, 42, 82], CYAN = [0, 150, 180], GRAY = [110, 120, 135];
    const logo = await loadLogo();
    if (logo) { try { doc.addImage(logo, "JPEG", 15, 12, 24, 24); } catch { /* ignore */ } }
    doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...NAVY);
    doc.text(COMPANY.name, 43, 18);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(COMPANY.addr1, 43, 24);
    doc.text(COMPANY.addr2, 43, 28);
    doc.text(`NPWP: ${COMPANY.npwp}`, 43, 32);
    doc.text(`${COMPANY.email}  |  ${COMPANY.phone}`, 43, 36);
    doc.setDrawColor(...CYAN); doc.setLineWidth(0.8); doc.line(15, 40, 195, 40);

    doc.setFont("helvetica", "bold"); doc.setFontSize(17); doc.setTextColor(...NAVY);
    doc.text("REKAP PAJAK TAHUNAN (SPT)", 15, 50);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...GRAY);
    doc.text(`Tahun Pajak: ${annual.year}`, 15, 57);
    doc.text(`Dicetak: ${new Date().toLocaleString("id-ID")}`, 15, 62);

    autoTable(doc, {
      startY: 68,
      head: [["Masa Pajak", "Transaksi", "Omzet Bruto", "PPh Final 0,5%", "Pendapatan Bersih"]],
      body: annual.months.map((m) => [m.label, String(m.count), fmt(m.revenue), fmt(m.pph), fmt(m.net)]),
      foot: [["TOTAL SETAHUN", String(annual.summary.total_transactions), fmt(annual.summary.total_revenue),
        fmt(annual.summary.total_pph), fmt(annual.summary.total_net)]],
      theme: "grid",
      headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", halign: "center" },
      footStyles: { fillColor: [34, 160, 90], textColor: 255, fontStyle: "bold" },
      columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
      styles: { fontSize: 9, cellPadding: 2.4 },
      margin: { left: 15, right: 15 },
    });

    let y = doc.lastAutoTable.finalY + 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...NAVY);
    doc.text(`Total PPh Final UMKM Terutang ${annual.year}: ${fmt(annual.summary.total_pph)}`, 15, y);
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(...GRAY);
    doc.text("*PPh Final UMKM 0,5% per masa pajak (bulan) sesuai PP No. 55 Tahun 2022 atas peredaran bruto. Nilai estimasi untuk penyusunan SPT Tahunan.", 15, y + 5);

    const ph = doc.internal.pageSize.getHeight();
    y = y + 20;
    if (y > ph - 30) { doc.addPage(); y = 30; }
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text("Hormat kami,", 150, y, { align: "center" });
    doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY);
    doc.text(COMPANY.name, 150, y + 18, { align: "center" });
    doc.setFontSize(7.5); doc.setTextColor(...GRAY); doc.setFont("helvetica", "normal");
    doc.text(`${COMPANY.name}  |  ${COMPANY.email}  |  ${COMPANY.phone}`, 15, ph - 8);

    doc.save(`rekap-pajak-tahunan-${annual.year}.pdf`);
  };

  const exportAnnualCSV = () => {
    if (!annual || !annual.months) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(["Rekap Pajak Tahunan (SPT) - PT. Ruang Karya Teknologi"].map(esc).join(","));
    lines.push([`Tahun Pajak: ${annual.year}`].map(esc).join(","));
    lines.push([]);
    lines.push(["Masa Pajak", "Transaksi", "Omzet Bruto (Rp)", "PPh Final 0,5% (Rp)", "Pendapatan Bersih (Rp)"].map(esc).join(","));
    annual.months.forEach((m) => lines.push([m.label, m.count, Math.round(m.revenue), Math.round(m.pph), Math.round(m.net)].map(esc).join(",")));
    lines.push(["TOTAL SETAHUN", annual.summary.total_transactions, Math.round(annual.summary.total_revenue), Math.round(annual.summary.total_pph), Math.round(annual.summary.total_net)].map(esc).join(","));
    lines.push([]);
    lines.push(["*PPh Final UMKM 0,5% per masa pajak sesuai PP No. 55 Tahun 2022 atas peredaran bruto"].map(esc).join(","));
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rekap-pajak-tahunan-${annual.year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };


  const chartData = (data?.series || []).map((s) => ({ name: s.label, v: Math.round(s.revenue) }));

  return (
    <div className="space-y-4" data-testid="admin-revenue">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-su-primary" />
          <h2 className="font-heading font-black text-lg">Laporan Pendapatan</h2>
        </div>
        <div className="flex items-center gap-2">
          {period === "annual" ? (
            <>
              <button data-testid="annual-export-pdf" onClick={exportAnnualPDF} disabled={!annual || !annual.months}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-secondary disabled:opacity-50">
                <FileText className="w-4 h-4" /> Unduh PDF SPT
              </button>
              <button data-testid="annual-export-csv" onClick={exportAnnualCSV} disabled={!annual || !annual.months}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-success disabled:opacity-50">
                <Download className="w-4 h-4" /> Unduh CSV
              </button>
              <button data-testid="annual-refresh" onClick={() => loadAnnual()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">
                <RefreshCw className="w-4 h-4" /> Muat Ulang
              </button>
            </>
          ) : (
            <>
              <button data-testid="revenue-export-pdf" onClick={exportPDF} disabled={!data || !data.series || !data.series.length}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-secondary disabled:opacity-50">
                <FileText className="w-4 h-4" /> Unduh PDF (Pajak)
              </button>
              <button data-testid="revenue-export-csv" onClick={exportCSV} disabled={!data || !data.series || !data.series.length}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-success disabled:opacity-50">
                <Download className="w-4 h-4" /> Unduh CSV
              </button>
              <button data-testid="revenue-refresh" onClick={() => load()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">
                <RefreshCw className="w-4 h-4" /> Muat Ulang
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="revenue-period-tabs">
        {PERIODS.map((p) => (
          <button key={p.id} data-testid={`revenue-period-${p.id}`} onClick={() => { setPeriod(p.id); if (p.id === "annual") loadAnnual(year); else load(p.id); }}
            className={`px-4 py-1.5 rounded-xl font-heading font-extrabold text-sm border-2 transition-all ${period === p.id ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {period === "annual" ? (
        <AnnualView annual={annual} year={year} setYear={setYear} loadAnnual={loadAnnual} />
      ) : (
      <>
      <div className="card-chunky p-3 flex flex-wrap items-end gap-3" data-testid="revenue-date-filter">
        <label className="flex flex-col gap-1 text-xs font-heading font-black text-su-secondary">
          Pilih Bulan (Masa Pajak)
          <input data-testid="revenue-month-picker" type="month" value={monthSel}
            onChange={(e) => pickMonth(e.target.value)}
            className="px-3 py-1.5 rounded-xl border-2 border-su-secondary/60 bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-secondary" />
        </label>
        <div className="w-px self-stretch bg-su-border mx-1 hidden sm:block" />
        <label className="flex flex-col gap-1 text-xs font-heading font-black text-su-muted">
          Dari
          <input data-testid="revenue-date-from" type="date" value={from}
            onChange={(e) => { setMonthSel(""); setFrom(e.target.value); load(period, e.target.value, to); }}
            className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-heading font-black text-su-muted">
          Sampai
          <input data-testid="revenue-date-to" type="date" value={to}
            onChange={(e) => { setMonthSel(""); setTo(e.target.value); load(period, from, e.target.value); }}
            className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary" />
        </label>
        <div className="flex gap-2">
          <button data-testid="revenue-preset-month" onClick={() => preset("thisMonth")} className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">Bulan Ini</button>
          <button data-testid="revenue-preset-year" onClick={() => preset("thisYear")} className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">Tahun Ini</button>
          <button data-testid="revenue-preset-all" onClick={() => preset("all")} className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">Semua Waktu</button>
        </div>
      </div>

      {data === null ? (
        <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>
      ) : data.error ? (
        <div className="card-chunky p-8 text-center text-su-muted font-medium">Gagal memuat laporan.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card-chunky p-5" data-testid="revenue-card-total">
              <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: "#a855f722" }}><Wallet className="w-6 h-6" style={{ color: "#a855f7" }} /></div>
              <p className="text-xs font-heading font-black uppercase text-su-muted">Total Pendapatan</p>
              <p className="font-heading font-black text-2xl">{fmt(data.summary.total_revenue)}</p>
            </div>
            <div className="card-chunky p-5" data-testid="revenue-card-count">
              <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: "var(--su-primary)22" }}><Receipt className="w-6 h-6 text-su-primary" /></div>
              <p className="text-xs font-heading font-black uppercase text-su-muted">Transaksi Berhasil</p>
              <p className="font-heading font-black text-2xl">{data.summary.total_transactions}</p>
            </div>
            <div className="card-chunky p-5" data-testid="revenue-card-avg">
              <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: "var(--su-success)22" }}><TrendingUp className="w-6 h-6 text-su-success" /></div>
              <p className="text-xs font-heading font-black uppercase text-su-muted">Rata-rata / Transaksi</p>
              <p className="font-heading font-black text-2xl">{fmt(data.summary.avg_transaction)}</p>
            </div>
          </div>

          <div className="card-chunky p-5" data-testid="revenue-tax">
            <div className="flex items-center gap-2 mb-3"><FileText className="w-5 h-5 text-su-secondary" /><h3 className="font-heading font-black">Ringkasan Pajak — PPh Final UMKM (0,5%)</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border-2 border-su-border p-3">
                <p className="text-xs font-heading font-black uppercase text-su-muted">Total Omzet (Bruto)</p>
                <p className="font-heading font-black text-lg" data-testid="tax-gross">{fmt(data.summary.total_revenue)}</p>
              </div>
              <div className="rounded-xl border-2 border-su-border p-3 bg-su-secondary/5">
                <p className="text-xs font-heading font-black uppercase text-su-muted">PPh Final UMKM (0,5%)</p>
                <p className="font-heading font-black text-lg text-su-secondary" data-testid="tax-pph">− {fmt(Math.round(data.summary.total_revenue * 0.005))}</p>
              </div>
              <div className="rounded-xl border-2 border-su-success/40 p-3 bg-su-success/10">
                <p className="text-xs font-heading font-black uppercase text-su-muted">Estimasi Bersih</p>
                <p className="font-heading font-black text-lg text-su-success" data-testid="tax-net">{fmt(Math.round(data.summary.total_revenue * 0.995))}</p>
              </div>
            </div>
            <p className="text-xs text-su-muted font-medium mt-3">*PPh Final UMKM 0,5% sesuai PP No. 55 Tahun 2022 atas peredaran bruto (omzet). Nilai bersifat estimasi.</p>
          </div>

          <div className="card-chunky p-6">
            <div className="flex items-center gap-2 mb-4"><CalendarDays className="w-5 h-5 text-su-primary" /><h3 className="font-heading font-black text-lg">Grafik Pendapatan ({PERIODS.find((p) => p.id === period)?.label})</h3></div>
            {chartData.length === 0 ? (
              <p className="text-su-muted font-medium text-sm py-8 text-center">Belum ada pendapatan pada rentang ini.</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--su-border)" />
                    <XAxis dataKey="name" tick={{ fill: "var(--su-muted)", fontWeight: 700, fontSize: 11 }} />
                    <YAxis tick={{ fill: "var(--su-muted)", fontSize: 11 }} tickFormatter={(v) => "Rp" + (v >= 1000000 ? (v / 1000000).toFixed(1) + "jt" : v >= 1000 ? (v / 1000).toFixed(0) + "rb" : v)} />
                    <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "var(--su-surface)", border: "2px solid var(--su-border)", borderRadius: 16 }} />
                    <Bar dataKey="v" fill="#a855f7" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="card-chunky overflow-x-auto" data-testid="revenue-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b-2 border-su-border">
                  <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Periode</th>
                  <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Transaksi</th>
                  <th className="p-3 font-heading font-black text-su-muted uppercase text-xs text-right">Pendapatan</th>
                </tr>
              </thead>
              <tbody>
                {(data.series || []).length === 0 ? (
                  <tr><td colSpan={3} className="p-6 text-center text-su-muted font-medium">Belum ada data.</td></tr>
                ) : data.series.map((s) => (
                  <tr key={s.key} className="border-b border-su-border/50" data-testid={`revenue-row-${s.key}`}>
                    <td className="p-3 font-semibold">{s.label}</td>
                    <td className="p-3 text-su-muted">{s.count}</td>
                    <td className="p-3 font-heading font-black text-right whitespace-nowrap">{fmt(s.revenue)}</td>
                  </tr>
                ))}
              </tbody>
              {(data.series || []).length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-su-border bg-su-primary/5">
                    <td className="p-3 font-heading font-black">TOTAL</td>
                    <td className="p-3 font-heading font-black">{data.summary.total_transactions}</td>
                    <td className="p-3 font-heading font-black text-right whitespace-nowrap text-su-success">{fmt(data.summary.total_revenue)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-chunky p-5" data-testid="revenue-by-gateway">
              <h3 className="font-heading font-black mb-3">Per Metode Pembayaran</h3>
              {(data.by_gateway || []).length === 0 ? <p className="text-su-muted text-sm font-medium">Belum ada data.</p> : (
                <div className="space-y-2">
                  {data.by_gateway.map((g) => (
                    <div key={g.gateway} className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{g.gateway} <span className="text-su-muted font-medium">({g.count})</span></span>
                      <span className="font-heading font-black">{fmt(g.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card-chunky p-5" data-testid="revenue-by-product">
              <h3 className="font-heading font-black mb-3">Per Produk</h3>
              {(data.by_product || []).length === 0 ? <p className="text-su-muted text-sm font-medium">Belum ada data.</p> : (
                <div className="space-y-2">
                  {data.by_product.map((p) => (
                    <div key={p.product} className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{p.product} <span className="text-su-muted font-medium">({p.count})</span></span>
                      <span className="font-heading font-black">{fmt(p.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

function AnnualView({ annual, year, setYear, loadAnnual }) {
  const changeYear = (y) => { setYear(y); loadAnnual(y); };
  if (annual === null) {
    return <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;
  }
  if (annual.error) {
    return <div className="card-chunky p-8 text-center text-su-muted font-medium">Gagal memuat rekap tahunan.</div>;
  }
  const s = annual.summary;
  return (
    <div className="space-y-4" data-testid="annual-view">
      <div className="card-chunky p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-heading font-black text-su-secondary">
          Tahun Pajak (SPT)
          <select data-testid="annual-year-select" value={year} onChange={(e) => changeYear(Number(e.target.value))}
            className="px-3 py-1.5 rounded-xl border-2 border-su-secondary/60 bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-secondary">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <p className="text-sm text-su-muted font-medium pb-1">Rekap 12 bulan (masa pajak) + total PPh Final UMKM setahun untuk penyusunan SPT Tahunan.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-chunky p-5" data-testid="annual-card-omzet">
          <p className="text-xs font-heading font-black uppercase text-su-muted">Omzet Bruto {year}</p>
          <p className="font-heading font-black text-xl">{fmt(s.total_revenue)}</p>
        </div>
        <div className="card-chunky p-5" data-testid="annual-card-count">
          <p className="text-xs font-heading font-black uppercase text-su-muted">Transaksi Setahun</p>
          <p className="font-heading font-black text-xl">{s.total_transactions}</p>
        </div>
        <div className="card-chunky p-5 bg-su-secondary/5" data-testid="annual-card-pph">
          <p className="text-xs font-heading font-black uppercase text-su-muted">Total PPh 0,5% Terutang</p>
          <p className="font-heading font-black text-xl text-su-secondary">{fmt(s.total_pph)}</p>
        </div>
        <div className="card-chunky p-5 bg-su-success/10" data-testid="annual-card-net">
          <p className="text-xs font-heading font-black uppercase text-su-muted">Pendapatan Bersih</p>
          <p className="font-heading font-black text-xl text-su-success">{fmt(s.total_net)}</p>
        </div>
      </div>

      <div className="card-chunky overflow-x-auto" data-testid="annual-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b-2 border-su-border">
              <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Masa Pajak</th>
              <th className="p-3 font-heading font-black text-su-muted uppercase text-xs text-center">Transaksi</th>
              <th className="p-3 font-heading font-black text-su-muted uppercase text-xs text-right">Omzet Bruto</th>
              <th className="p-3 font-heading font-black text-su-muted uppercase text-xs text-right">PPh Final 0,5%</th>
              <th className="p-3 font-heading font-black text-su-muted uppercase text-xs text-right">Pendapatan Bersih</th>
            </tr>
          </thead>
          <tbody>
            {annual.months.map((m) => (
              <tr key={m.month} className="border-b border-su-border/50" data-testid={`annual-row-${m.month}`}>
                <td className="p-3 font-semibold">{m.label} {year}</td>
                <td className="p-3 text-su-muted text-center">{m.count}</td>
                <td className="p-3 font-heading font-black text-right whitespace-nowrap">{fmt(m.revenue)}</td>
                <td className="p-3 text-right whitespace-nowrap text-su-secondary font-heading font-black">{fmt(m.pph)}</td>
                <td className="p-3 text-right whitespace-nowrap">{fmt(m.net)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-su-border bg-su-primary/5">
              <td className="p-3 font-heading font-black">TOTAL SETAHUN</td>
              <td className="p-3 font-heading font-black text-center">{s.total_transactions}</td>
              <td className="p-3 font-heading font-black text-right whitespace-nowrap">{fmt(s.total_revenue)}</td>
              <td className="p-3 font-heading font-black text-right whitespace-nowrap text-su-secondary">{fmt(s.total_pph)}</td>
              <td className="p-3 font-heading font-black text-right whitespace-nowrap text-su-success">{fmt(s.total_net)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-su-muted font-medium">*PPh Final UMKM 0,5% per masa pajak (bulan) sesuai PP No. 55 Tahun 2022 atas peredaran bruto. Nilai bersifat estimasi untuk penyusunan SPT Tahunan.</p>
    </div>
  );
}
